import type { BeforeAfterProject, ProjectImage } from "@/config/projects";
import { findServiceArea } from "@/config/service-areas";
import { serviceBySlug } from "@/config/services";
import type { LocaleCode } from "./locale-config";
import { localizeServiceArea, localizeServicePage } from "./content";

function localizeImage(
  image: ProjectImage,
  locale: "fa-ir" | "ar-sa",
  phase: "before" | "after",
  index: number,
  serviceName: string,
  areaName: string,
): ProjectImage {
  const before = phase === "before";
  return {
    ...image,
    alt:
      locale === "fa-ir"
        ? `نمای ${before ? "پیش از کار" : "پس از کار"} در نمونهٔ ${serviceName} برای خانه‌ای در ${areaName}`
        : `صورة ${before ? "قبل العمل" : "بعد العمل"} في نموذج ${serviceName} لعقار في ${areaName}`,
    caption:
      locale === "fa-ir"
        ? `نمای ${before ? "پیش از کار" : "پس از کار"} ${index + 1}`
        : `صورة ${before ? "قبل العمل" : "بعد العمل"} ${index + 1}`,
  };
}

export function localizeProject(
  project: BeforeAfterProject,
  locale: LocaleCode,
): BeforeAfterProject {
  if (locale !== "fa-ir" && locale !== "ar-sa") return project;

  const baseService = project.service ? serviceBySlug[project.service] : undefined;
  const service = baseService ? localizeServicePage(baseService, locale) : undefined;
  const baseArea = project.city ? findServiceArea(project.city) : null;
  const area = baseArea ? localizeServiceArea(baseArea, locale) : null;
  const serviceName = service?.shortTitle ?? (locale === "fa-ir" ? "خدمات سقف" : "خدمة تسقيف");
  const areaName = area?.name ?? (locale === "fa-ir" ? "مترو ونکوور" : "مترو فانكوفر");
  const warningSigns = service?.warningSigns.slice(0, 3) ?? [];
  const evaluates = service?.evaluates.slice(0, 3) ?? [];
  const beforeImages = project.beforeImages.map((image, index) =>
    localizeImage(image, locale, "before", index, serviceName, areaName),
  );
  const afterImages = project.afterImages.map((image, index) =>
    localizeImage(image, locale, "after", index, serviceName, areaName),
  );
  const featuredImage =
    afterImages.find((image) => image.src === project.featuredImage.src) ??
    beforeImages.find((image) => image.src === project.featuredImage.src) ??
    afterImages[0] ??
    beforeImages[0] ??
    project.featuredImage;
  const before = beforeImages.find((image) => image.src === project.before.src) ?? beforeImages[0]!;
  const after = afterImages.find((image) => image.src === project.after.src) ?? afterImages[0]!;

  if (locale === "fa-ir") {
    return {
      ...project,
      title: `نمونهٔ قبل و بعد: ${serviceName}`,
      location: `نمونه‌ای تصویری از خانه‌های ${areaName} در مترو ونکوور`,
      system: serviceName,
      roofType: serviceName,
      shortProblem: `نمونه‌ای تصویری از شرایط رایج نیازمند ${serviceName} در ${areaName}.`,
      problem: warningSigns.length
        ? `در این نمونهٔ تصویری، نشانه‌های قابل‌بررسی شامل ${warningSigns.join("، ")} است. این تصاویر نمونهٔ برنامه‌ریزی‌اند و گزارش یک پروژهٔ واقعی رولین نیستند.`
        : `این نمونهٔ تصویری، شرایطی را نشان می‌دهد که ممکن است به ${serviceName} نیاز داشته باشد. نتیجهٔ قطعی فقط پس از ارزیابی همان ملک مشخص می‌شود.`,
      diagnosis: evaluates.length
        ? `برای انتخاب مسیر درست، ${evaluates.join("، ")} بررسی می‌شود. تصمیم نهایی بر پایهٔ وضعیت واقعی سقف و جزئیات قابل‌مشاهده گرفته می‌شود.`
        : `ارزیابی حضوری، وضعیت پوشش سقف، نقاط اتصال و مسیر زهکشی را مشخص می‌کند.`,
      solution: `راه‌حل پس از ارزیابی مشخص می‌شود و می‌تواند شامل ${serviceName} با مصالح سازگار و جزئیات اجرایی متناسب با ساختمان باشد. شرح دقیق کار پیش از اجرا به‌صورت مکتوب ارائه می‌شود.`,
      materials: service?.materials ?? [],
      beforeImages,
      afterImages,
      before,
      after,
      featuredImage,
    };
  }

  return {
    ...project,
    title: `نموذج قبل وبعد: ${serviceName}`,
    location: `نموذج بصري لطراز سكني في ${areaName} ضمن مترو فانكوفر`,
    system: serviceName,
    roofType: serviceName,
    shortProblem: `نموذج بصري لظروف شائعة قد تستدعي ${serviceName} في ${areaName}.`,
    problem: warningSigns.length
      ? `تظهر في هذا النموذج البصري مؤشرات تستحق المراجعة، منها ${warningSigns.join("، ")}. الصور مخصصة للتخطيط وليست توثيقاً لمشروع نفذته رولين.`
      : `يعرض هذا النموذج البصري حالة قد تستدعي ${serviceName}. ولا تُحسم التوصية إلا بعد تقييم العقار نفسه.`,
    diagnosis: evaluates.length
      ? `لتحديد المسار المناسب، تُراجع عناصر تشمل ${evaluates.join("، ")}. ويستند القرار النهائي إلى حالة السقف الفعلية والتفاصيل الظاهرة.`
      : `توضح المعاينة الميدانية حالة غطاء السقف ونقاط الانتقال ومسارات التصريف.`,
    solution: `يُحدَّد الحل بعد التقييم، وقد يشمل ${serviceName} بمواد متوافقة وتفاصيل تنفيذ تناسب المبنى. ويُقدَّم نطاق العمل الدقيق كتابةً قبل التنفيذ.`,
    materials: service?.materials ?? [],
    beforeImages,
    afterImages,
    before,
    after,
    featuredImage,
  };
}
